import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '@/components/Header';
import TodayView from '@/components/TodayView';
import WeekView from '@/components/WeekView';
import WeeklySummary from '@/components/WeeklySummary';
import QuickCapture from '@/components/QuickCapture';
import {
  CheckCircle,
  Clock,
  FileText,
  WarningCircle,
  Plus,
  ArrowRight,
  CalendarBlank,
  ListChecks,
  ChartBar
} from '@phosphor-icons/react';
import { format, parseISO, isValid } from 'date-fns';

const API = `${window.location.origin}/api`;

const TABS = [
  { id: 'overview', label: 'Overview', icon: ChartBar },
  { id: 'today', label: "Today's Focus", icon: CalendarBlank },
  { id: 'week', label: "This Week's Log", icon: ListChecks },
  { id: 'summary', label: 'Weekly Summary', icon: CheckCircle },
];

function Dashboard({ user }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchSummary();
    }
  }, [activeTab]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard/summary`, { withCredentials: true });
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'MMM d, yyyy') : 'Invalid date';
    } catch { return 'Invalid date'; }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header user={user} />

      <main className="max-w-7xl mx-auto p-6 md:p-8">
        {/* Welcome */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-slate-900 mb-1">
            Welcome back, {user?.name?.split(' ')[0] || 'there'}
          </h1>
          <p className="text-sm md:text-base text-slate-500">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
        </motion.div>

        {/* Quick Capture */}
        <div className="mb-6" data-testid="dashboard-quick-capture">
          <QuickCapture onCaptured={() => navigate('/inbox')} />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1 mb-6 overflow-x-auto" data-testid="dashboard-tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-navy text-white shadow-sm'
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon size={16} weight={activeTab === tab.id ? 'fill' : 'regular'} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <OverviewTab summary={summary} loading={loading} navigate={navigate} formatDate={formatDate} user={user} />
        )}
        {activeTab === 'today' && <TodayView />}
        {activeTab === 'week' && <WeekView />}
        {activeTab === 'summary' && <WeeklySummary />}
      </main>
    </div>
  );
}

function OverviewTab({ summary, loading, navigate, formatDate }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-navy border-t-transparent"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* Stats — Control Room Grid: F-pattern with critical metrics first */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Tasks', value: summary?.stats?.total_tasks || 0, icon: CheckCircle, color: 'text-navy', path: '/tasks', filter: null },
          { label: 'Pending', value: summary?.stats?.pending_tasks || 0, icon: Clock, color: 'text-navy', path: '/tasks', filter: 'pending' },
          { label: 'Completed', value: summary?.stats?.completed_tasks || 0, icon: CheckCircle, color: 'text-emerald-600', path: '/tasks', filter: 'completed' },
        ].map((stat, i) => (
          <button
            key={i}
            onClick={() => navigate(stat.path, { state: { filterStatus: stat.filter } })}
            className="bg-white border border-slate-200 rounded-lg p-6 transition-all duration-200 hover:shadow-sm hover:-translate-y-[1px] hover:border-navy/30 active:translate-y-0 active:shadow-none focus:outline-none focus:ring-2 focus:ring-navy focus:ring-offset-2 text-left cursor-pointer w-full"
            data-testid={`stats-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <div className="flex items-center gap-2.5 mb-1.5">
              <stat.icon size={18} weight="duotone" className={stat.color} />
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">{stat.label}</p>
            </div>
            <p className="text-3xl font-heading font-semibold text-slate-900">{stat.value}</p>
          </button>
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Upcoming Tasks */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-6" data-testid="todays-tasks-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-heading font-medium text-slate-900">Upcoming Tasks</h2>
            <button onClick={() => navigate('/tasks')} className="text-slate-400 hover:text-navy transition-colors" data-testid="view-all-tasks-btn">
              <ArrowRight size={18} />
            </button>
          </div>
          {summary?.today_tasks?.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center" data-testid="no-tasks-today">No upcoming tasks</p>
          ) : (
            <div className="space-y-2">
              {summary?.today_tasks?.map((task) => (
                <div key={task.task_id} className="flex items-start gap-3 p-3 border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`task-item-${task.task_id}`}>
                  <CheckCircle size={18} weight="regular" className="text-slate-300 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-900">{task.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {task.project_client && <span className="text-xs text-navy font-medium">{task.project_client}</span>}
                      {task.due_date && <span className="text-xs text-slate-400">{formatDate(task.due_date)}</span>}
                    </div>
                  </div>
                  {task.priority === 'high' && <span className="text-[11px] font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-md border border-red-200">High</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Urgent */}
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="urgent-tasks-section">
          <div className="flex items-center gap-2 mb-4">
            <WarningCircle size={18} weight="duotone" className="text-red-600" />
            <h2 className="text-base font-heading font-medium text-slate-900">Urgent</h2>
          </div>
          {summary?.urgent_tasks?.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="no-urgent-tasks">No urgent tasks</p>
          ) : (
            <div className="space-y-2">
              {summary?.urgent_tasks?.slice(0, 3).map((task) => (
                <div key={task.task_id} className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid={`urgent-task-${task.task_id}`}>
                  <p className="font-medium text-sm text-slate-900">{task.title}</p>
                  {task.due_date && <p className="text-xs text-red-600 mt-1">{formatDate(task.due_date)}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Documents + Expiring */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="recent-documents-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-heading font-medium text-slate-900">Recent Documents</h2>
            <button onClick={() => navigate('/documents')} className="text-slate-400 hover:text-navy transition-colors" data-testid="view-all-docs-btn">
              <ArrowRight size={18} />
            </button>
          </div>
          {summary?.recent_documents?.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="no-documents">No documents yet</p>
          ) : (
            <div className="space-y-2">
              {summary?.recent_documents?.slice(0, 4).map((doc) => (
                <div key={doc.document_id} className="flex items-center gap-3 p-3 border border-slate-100 rounded-lg" data-testid={`doc-item-${doc.document_id}`}>
                  <FileText size={18} weight="duotone" className="text-navy" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-slate-900 truncate">{doc.original_filename}</p>
                    <p className="text-xs text-slate-400">{doc.category}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {summary?.expiring_documents?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="expiring-docs-section">
            <div className="flex items-center gap-2 mb-4">
              <WarningCircle size={18} weight="duotone" className="text-red-600" />
              <h2 className="text-base font-heading font-medium text-slate-900">Expiring Soon</h2>
            </div>
            <div className="space-y-2">
              {summary.expiring_documents.map((doc) => (
                <div key={doc.document_id} className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid={`expiring-doc-${doc.document_id}`}>
                  <p className="font-medium text-sm text-slate-900 truncate">{doc.original_filename}</p>
                  <p className="text-xs text-red-600 mt-1">Expires: {formatDate(doc.expiry_date)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <button onClick={() => navigate('/tasks')} className="bg-navy text-white hover:bg-navy-hover transition-all duration-200 font-medium px-4 py-2 rounded-lg flex items-center gap-2 text-sm shadow-sm hover:shadow-md" data-testid="create-task-btn">
          <Plus size={16} weight="bold" /> Create Task
        </button>
        <button onClick={() => navigate('/documents')} className="bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:shadow-sm transition-all duration-200 font-medium px-4 py-2 rounded-lg flex items-center gap-2 text-sm" data-testid="upload-document-btn">
          <FileText size={16} /> Upload Document
        </button>
        <button onClick={() => navigate('/ai-assistant')} className="bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:shadow-sm transition-all duration-200 font-medium px-4 py-2 rounded-lg text-sm" data-testid="ask-ai-btn">
          Ask AI Assistant
        </button>
      </div>
    </motion.div>
  );
}

export default Dashboard;
